/* Frontier Designs · native Prestige product form + MNML product metafields. */
(() => {
  if (customElements.get('fd-bulk-order')) return;
  class FrontierBulkOrder extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.abort = new AbortController();
      this.config = JSON.parse(this.querySelector('[data-fbo-config]').textContent);
      this.form = document.getElementById(this.dataset.formId);
      if (!this.form) { this.fail('The product form could not be found.'); return; }
      this.input = this.querySelector('[data-quantity]');
      this.slider = this.querySelector('[data-slider]');
      this.track = this.querySelector('[data-tier-track]');
      this.viewport = this.querySelector('[data-tier-viewport]');
      this.file = this.querySelector('[data-logo-file]');
      this.submit = this.querySelector('.fbo-buy button[type="submit"]');
      this.variant = this.config.variant;
      this.cartItems = [];
      this.cartReady = false;
      this.min = Math.max(1, Math.round(Number(this.config.minimum) || 10));
      this.max = Math.max(this.min, Math.round(Number(this.config.maximum) || 2000));
      this.q = this.clamp(this.config.initial);
      this.input.min = this.min;
      this.input.max = this.max;
      this.input.value = this.q;
      this.querySelector('[data-scale-min]').textContent = this.min.toLocaleString();
      this.querySelector('[data-scale-max]').textContent = this.max.toLocaleString();
      const raw = this.config.tiers;
      this.combineVariants = Boolean(raw?.combineVariantQuantities ?? this.config.combineVariants);
      try {
        this.tiers = this.normalizeTiers(raw);
        if (this.config.currency !== this.config.shopCurrency) throw new Error('Bulk pricing is currently available in '+this.config.shopCurrency+'. Please select that currency to order.');
        this.pricingReady = true;
      } catch (error) {
        this.pricingReady = false;
        this.fail(error.message);
      }
      this.form.enctype = 'multipart/form-data';
      const on = (target, name, handler, options = {}) => target?.addEventListener(name, handler, {...options, signal:this.abort.signal});
      on(this.input,'input',()=>{
        if (!this.input.validity.valid || this.input.value === '') { this.updateLock(); return; }
        this.q = this.clamp(this.input.value);
        this.render(false);
        this.input.dispatchEvent(new CustomEvent('frontier:quantity-change',{bubbles:true,detail:{quantity:this.q}}));
      });
      on(this.input,'change',()=>{ this.q=this.clamp(this.input.value);this.input.value=this.q;this.render(); });
      on(this.querySelector('[data-minus]'),'click',()=>this.setQuantity(this.q-1));
      on(this.querySelector('[data-plus]'),'click',()=>this.setQuantity(this.q+1));
      on(this.slider,'input',()=>{
        let value = this.valueFromPosition(Number(this.slider.value));
        if (this.config.snap) {
          const closest = this.anchors.reduce((best,q)=>Math.abs(this.positionFor(q)-Number(this.slider.value)) < Math.abs(this.positionFor(best)-Number(this.slider.value)) ? q:best,this.anchors[0]);
          if (Math.abs(this.positionFor(closest)-Number(this.slider.value)) <= Number(this.config.snapPercent)*100) value=closest;
        }
        this.q=this.clamp(value);
        this.input.value=this.q;
        this.render(false,false);
      });
      on(this.slider,'change',()=>{ this.render(); this.input.dispatchEvent(new Event('change',{bubbles:true})); });
      // Stop an in-flight animation before hit-testing a new press. Focusing a
      // card must never scroll it away between pointerdown and click.
      on(this.viewport,'pointerdown',event=>{
        const left=this.viewport.scrollLeft;
        this.viewport.scrollTo?.({left,behavior:'instant'});
        const button=event.target.closest('[data-tier-quantity]');
        if(button&&this.track.contains(button)&&event.pointerType!=='touch')event.preventDefault();
      });
      on(this.track,'click',event=>{
        const button=event.target.closest('[data-tier-quantity]');
        if(!button||!this.track.contains(button))return;
        const quantity=Number(button.dataset.tierQuantity);
        if(!Number.isFinite(quantity))return;
        // Commit quantity, unit price, total, thumb and highlight in one render.
        this.setQuantity(quantity-this.existingQuantity());
        button.focus({preventScroll:true});
      });
      // Native Tab / Enter / Space behavior is retained. There is intentionally
      // no focusin recentering: it moved the target before click could fire.
      on(this.form,'variant:change',event=>{
        this.variant=event.detail.variant;
        this.submit=this.querySelector('.fbo-buy button[type="submit"]');
        this.render();
      });
      on(document,'cart:change',event=>{
        if(event.detail?.cart?.items) { this.cartItems=event.detail.cart.items; this.cartReady=true; this.render(); }
        else this.loadCart();
      });
      on(this.form,'variant:add',()=>this.render());
      on(this.form,'cart:error',()=>this.updateLock());
      // Capture before Prestige sends its FormData, including submissions from a sticky bar.
      on(this.form,'submit',event=>{
        this.updateLock();
        if (!this.canSubmit()) {
          event.preventDefault(); event.stopImmediatePropagation();
          if (this.file && this.config.uploadRequired && !this.file.files.length) this.file.reportValidity();
          else this.input.reportValidity();
        }
      },{capture:true});
      this.bindUpload(on);
      if(this.pricingReady) this.buildTiers();
      this.resize = new ResizeObserver(()=>this.positionTrack());
      this.resize.observe(this.viewport);
      this.render();
      this.loadCart();
    }
    disconnectedCallback() {
      this.abort?.abort(); this.resize?.disconnect(); this.previewUrls?.forEach(url=>URL.revokeObjectURL(url)); this.initialized=false;
    }
    clamp(value) { return Math.max(this.min,Math.min(this.max,Math.round(Number(value)||this.min))); }
    normalizeTiers(raw) {
      const data=Array.isArray(raw)?raw:raw?.tiers;
      if(!Array.isArray(data)||!data.length) throw new Error('Bulk pricing is not configured for this product yet. Please contact us for a quote.');
      const unique=new Map();
      for(const tier of data) {
        const qty=Number(tier.qty),amount=Number(tier.price),type=tier.type;
        if(!Number.isInteger(qty)||qty<1||!Number.isFinite(amount)||amount<0||!['amount','percentage'].includes(type)||(type==='percentage'&&amount>100)) {
          throw new Error('The pricing data needs checking before this product can be ordered. Please contact us for a quote.');
        }
        unique.set(qty,{qty,amount,type});
      }
      const tiers=[...unique.values()].sort((a,b)=>a.qty-b.qty);
      if(tiers[0].qty>this.min) this.min=tiers[0].qty;
      if(this.min>this.max) throw new Error('The minimum order exceeds the maximum quantity. Please contact us.');
      this.input.min=this.min;
      this.q=this.clamp(this.q);
      this.input.value=this.q;
      this.querySelector('[data-scale-min]').textContent=this.min.toLocaleString();
      return tiers;
    }
    money(cents) {
      return new Intl.NumberFormat(document.documentElement.lang||'en-US',{style:'currency',currency:this.config.currency}).format(cents/100);
    }
    existingQuantity() {
      if(!this.config.includeCart)return 0;
      return this.cartItems.reduce((sum,item)=>{
        const matches=this.combineVariants?String(item.product_id)===String(this.config.productId):String(item.variant_id)===String(this.variant?.id);
        return sum+(matches?Number(item.quantity):0);
      },0);
    }
    unitPrice(quantity) {
      const base=Number(this.variant?.price)||0;
      const tier=this.tiers?.filter(t=>t.qty<=quantity).at(-1);
      if(!tier) return base;
      // MNML amount tiers contain the final unit price in cents, not money off.
      return Math.max(0,tier.type==='percentage'?Math.round(base*(1-tier.amount/100)):Math.round(tier.amount));
    }
    buildTiers() {
      this.track.replaceChildren();
      this.tiers.filter(t=>t.qty<=this.max).forEach(tier=>{
        const button=document.createElement('button');
        button.type='button';button.dataset.tierQuantity=tier.qty;button.setAttribute('aria-pressed','false');
        const quantity=document.createElement('strong');quantity.textContent=tier.qty.toLocaleString();quantity.style.fontWeight='inherit';
        const price=document.createElement('span');price.textContent=this.money(this.unitPrice(tier.qty))+'/ea';
        button.append(quantity,price);this.track.append(button);
      });
      this.anchors=[...new Set([this.min,...this.tiers.map(t=>t.qty).filter(q=>q>=this.min&&q<=this.max),this.max])].sort((a,b)=>a-b);

    }
    positionFor(q) {
      const a=this.anchors;
      if(!a?.length||a.length===1) return 0;
      let index=a.findLastIndex(n=>n<=q);index=Math.max(0,index);
      if(index===a.length-1) return 10000;
      return (index+(q-a[index])/(a[index+1]-a[index]))/(a.length-1)*10000;
    }
    valueFromPosition(value) {
      const a=this.anchors;
      if(!a?.length) return this.min;
      if(a.length===1) return a[0];
      const position=value/10000*(a.length-1),i=Math.min(a.length-2,Math.floor(position));
      return Math.round(a[i]+(position-i)*(a[i+1]-a[i]));
    }
    setQuantity(value) {
      this.q=this.clamp(value);this.input.value=this.q;this.render();
      this.input.dispatchEvent(new Event('change',{bubbles:true}));
    }
    positionTrack(forceIndex) {
      if(!this.track||!this.pricingReady) return;
      const boxes=[...this.track.querySelectorAll('button')];if(!boxes.length)return;
      const total=this.q+this.existingQuantity();
      let index=Math.max(0,boxes.findLastIndex(b=>Number(b.dataset.tierQuantity)<=total));
      if(index<boxes.length-1){const a=Number(boxes[index].dataset.tierQuantity),b=Number(boxes[index+1].dataset.tierQuantity);index+=(total-a)/(b-a);}
      if(Number.isFinite(forceIndex))index=forceIndex;
      // Measure actual cards: their size follows the available product-column width.
      const width=boxes[0].getBoundingClientRect().width;
      const gap=parseFloat(getComputedStyle(this.track).columnGap)||7;
      const viewportWidth=this.viewport.clientWidth||this.viewport.getBoundingClientRect().width;
      const extent=boxes.length*(width+gap)-gap;
      const left=Math.max(0,Math.min(Math.max(0,extent-viewportWidth),index*(width+gap)+width/2-viewportWidth/2));
      if(this.viewport.scrollTo)this.viewport.scrollTo({left,behavior:window.matchMedia?.('(prefers-reduced-motion: reduce)').matches?'instant':'smooth'});
      else this.viewport.scrollLeft=left;
    }
    render(syncInput=true,syncSlider=true) {
      if(!this.input)return;
      if(syncInput)this.input.value=this.q;
      this.querySelector('[data-minus]').disabled=this.q<=this.min;
      this.querySelector('[data-plus]').disabled=this.q>=this.max;
      if(this.pricingReady) {
        if(syncSlider)this.slider.value=this.positionFor(this.q);
        this.slider.style.setProperty('--fbo-slider-progress',(Number(this.slider.value)/100)+'%');
        this.slider.setAttribute('aria-valuetext',this.q.toLocaleString()+' ornaments');
        const existing=this.existingQuantity(),effective=this.q+existing;
        const basis=this.querySelector('[data-pricing-basis]');
        if(basis){basis.hidden=!existing;basis.textContent=`${this.q.toLocaleString()} to add + ${existing.toLocaleString()} in cart = ${effective.toLocaleString()} qualifying ornaments`;}
        const unit=this.unitPrice(effective),total=unit*this.q;
        const unitElement=this.querySelector('[data-unit]');
        unitElement.textContent=this.money(unit);
        const original=this.querySelector('[data-unit-original]');
        const base=Number(this.variant?.price)||0;
        const discounted=unit<base;
        unitElement.classList.toggle('is-discounted',discounted);
        if(original){original.hidden=!discounted;original.textContent=this.money(base);}
        this.querySelector('[data-total]').textContent=this.money(total);
        const active=this.tiers.filter(t=>t.qty<=effective).at(-1)?.qty;
        this.track.querySelectorAll('button').forEach(button=>{
          const threshold=Number(button.dataset.tierQuantity);
          button.setAttribute('aria-pressed',String(threshold===active));
          button.querySelector('span').textContent=this.money(this.unitPrice(threshold))+'/ea';
        });
        const next=this.tiers.find(t=>t.qty>effective);
        const nextElement=this.querySelector('[data-next]');
        if(nextElement){
          const nextUnit=next?this.unitPrice(next.qty):unit;
          nextElement.hidden=!next;
          nextElement.textContent=next&&!nextElement.hidden?`Add ${(next.qty-effective).toLocaleString()} more for ${this.money(nextUnit)} each.`:'';
        }
        const status=this.querySelector('[data-cart-status]');
        status.hidden=!existing;status.textContent=`${existing.toLocaleString()} qualifying ornaments already in your cart. Pricing includes those quantities; this total is for the ${this.q.toLocaleString()} being added.`;
        this.positionTrack();
        if(this.submit&&!this.submit.hasAttribute('aria-busy'))this.submit.textContent=this.variant?.available?`Add ${this.q.toLocaleString()} ornaments to cart${this.config.showButtonTotal?' · '+this.money(total):''}`:'Sold out';
      }
      this.updateLock();
    }
    canSubmit() {
      return Boolean(this.pricingReady&&this.cartReady&&this.variant?.available&&this.input.validity.valid&&(!this.file||this.file.validity.valid));
    }
    updateLock() {
      if(!this.submit)return;
      this.submit.disabled=this.submit.hasAttribute('aria-busy')||!this.canSubmit();
      this.slider.disabled=!this.pricingReady;
    }
    fail(message) {
      const error=this.querySelector('[data-price-error]');
      error.hidden=false;error.textContent=message;
      this.pricingReady=false;
      this.querySelectorAll('button[type="submit"]').forEach(b=>b.disabled=true);
    }
    async loadCart() {
      try {
        const response=await fetch((window.Shopify?.routes?.root||'/')+'cart.js',{headers:{Accept:'application/json'},signal:this.abort.signal});
        if(!response.ok)throw new Error('Cart unavailable');
        const cart=await response.json();
        this.cartItems=cart.items||[];this.cartReady=true;this.render();
      } catch(error) {
        if(error.name==='AbortError')return;
        this.cartReady=false;
        const warning=this.querySelector('[data-price-error]');warning.hidden=false;warning.textContent='We could not check your cart. Refresh the page before ordering.';
        this.updateLock();
      }
    }
    bindUpload(on) {
      if(!this.file)return;
      const zone=this.querySelector('[data-dropzone]'),browse=this.querySelector('[data-choose-file]'),remove=this.querySelector('[data-remove-file]'),error=this.querySelector('[data-file-error]');
      const slots=[...this.querySelectorAll('[data-logo-slot]')];
      const previews=this.querySelector('[data-file-previews]');
      this.selectedFiles=[];this.previewUrls=[];
      const showError=message=>{error.hidden=!message;error.textContent=message;};
      const fileList=files=>{const transfer=new DataTransfer();files.forEach(file=>transfer.items.add(file));return transfer.files;};
      const sync=()=>{
        this.file.files=fileList(this.selectedFiles);
        slots.forEach((slot,index)=>{slot.files=fileList(this.selectedFiles[index]?[this.selectedFiles[index]]:[]);slot.disabled=!this.selectedFiles[index];});
        this.file.setCustomValidity('');showError('');
        const count=this.selectedFiles.length;
        this.querySelector('[data-file-empty]').hidden=Boolean(count);
        this.querySelector('[data-file-selected]').hidden=!count;
        this.querySelector('[data-file-name]').textContent=`${count} of 3 files selected`;
        remove.hidden=!count;browse.disabled=count===3;browse.textContent=count?'Add files':'Choose files';
        this.previewUrls.forEach(url=>URL.revokeObjectURL(url));this.previewUrls=[];
        if(previews){
          previews.replaceChildren();previews.hidden=!count;
          this.selectedFiles.forEach((file,index)=>{
            const card=document.createElement('div');card.className='fbo-file-preview';
            const extension=file.name.split('.').at(-1).toLowerCase();
            if(['png','jpg','jpeg','webp','gif','avif','bmp'].includes(extension)){
              const image=document.createElement('img');const url=URL.createObjectURL(file);this.previewUrls.push(url);
              image.src=url;image.alt=file.name;image.width=44;image.height=44;card.append(image);
            }else{const icon=document.createElement('span');icon.className='fbo-file-type';icon.textContent=extension.toUpperCase();card.append(icon);}
            const name=document.createElement('span');name.className='fbo-file-preview-name';name.textContent=file.name;name.title=file.name;
            const button=document.createElement('button');button.type='button';button.textContent='×';button.setAttribute('aria-label','Remove '+file.name);
            on(button,'click',()=>{this.selectedFiles.splice(index,1);sync();});card.append(name,button);previews.append(card);
          });
        }
        this.updateLock();
      };
      const add=incoming=>{
        const files=[...this.selectedFiles];
        for(const file of incoming){if(!files.some(f=>f.name===file.name&&f.size===file.size&&f.lastModified===file.lastModified))files.push(file);}
        const extensions=this.file.accept.split(',').map(s=>s.trim().toLowerCase()).filter(Boolean);
        let message=files.length>3?'You can attach up to 3 files. Remove a file before adding another.':'';
        for(const file of files){
          if(extensions.length&&!extensions.includes('.'+file.name.split('.').at(-1).toLowerCase()))message='Please choose an accepted logo file type.';
          if(file.size>Number(this.config.uploadMaxMB)*1024*1024)message=`Each file must be smaller than ${this.config.uploadMaxMB} MB.`;
        }
        if(message){sync();showError(message);return;}
        this.selectedFiles=files;sync();
      };
      on(browse,'click',()=>this.file.click());
      // The chooser is not submitted: three distinct file properties carry all attachments.
      on(this.file,'change',()=>add([...this.file.files]));
      on(this.file,'cancel',sync);
      on(remove,'click',()=>{this.selectedFiles=[];sync();});
      ['dragenter','dragover'].forEach(type=>on(zone,type,event=>{event.preventDefault();zone.classList.add('is-dragging');}));
      on(zone,'dragleave',()=>zone.classList.remove('is-dragging'));
      on(zone,'drop',event=>{event.preventDefault();zone.classList.remove('is-dragging');add([...event.dataTransfer.files]);});
      sync();
    }
  }
  customElements.define('fd-bulk-order',FrontierBulkOrder);
})();

/* Gallery and native contact-form behavior for the standalone page section. */
(() => {
  if (customElements.get('fd-bulk-page')) return;
  class FDBulkPage extends HTMLElement {
    connectedCallback() {
      if(this.pageReady)return;
      this.pageReady=true;
      this.pageAbort=new AbortController();
      const thumbnails=this.querySelector('.fbp-thumbnails');
      thumbnails?.addEventListener('pointerdown',event=>{
        // Keep a moving thumbnail under the pointer until its click completes.
        thumbnails.scrollTo({left:thumbnails.scrollLeft,behavior:'instant'});
        const button=event.target.closest('[data-gallery-image]');
        if(button&&event.pointerType!=='touch')event.preventDefault();
      },{signal:this.pageAbort.signal});
      if(thumbnails){
        this.galleryResize=new ResizeObserver(()=>this.revealThumbnail(this.querySelector('.fbp-thumbnails [aria-pressed="true"]'),true));
        this.galleryResize.observe(thumbnails);
        this.revealThumbnail(this.querySelector('.fbp-thumbnails [aria-pressed="true"]'),true);
      }
      this.addEventListener('click',event=>{
        const button=event.target.closest('[data-gallery-image]');
        if(!button||!this.contains(button))return;
        this.selectImage(button.dataset.galleryImage,button.dataset.imageAlt,button.dataset.zoom||1,button);
      },{signal:this.pageAbort.signal});
      this.addEventListener('variant:change',event=>{
        if(event.detail?.formId!==this.dataset.formId)return;
        const variant=event.detail.variant;
        const image=variant?.featured_image||variant?.featured_media?.preview_image;
        if(!image?.src)return;
        const button=[...this.querySelectorAll('[data-image-id]')].find(b=>String(b.dataset.imageId)===String(image.id));
        // Keep intentional sample images while placeholder mode is enabled.
        if(this.querySelector('[data-image-id]'))this.selectImage(image.src,image.alt||'',1,button);
      },{signal:this.pageAbort.signal});
      this.querySelectorAll('.fbp-contact-details').forEach(details=>{
        if(details.querySelector('.fbp-form-errors,.fbp-form-success'))details.open=true;
      });
    }
    selectImage(src,alt,zoom,button) {
      const image=this.querySelector('.fbp-main-photo img');if(!image)return;
      image.removeAttribute('srcset');image.removeAttribute('sizes');
      image.src=src;image.alt=alt||'Wood ornament product photo';image.style.transform=`scale(${Number(zoom)||1})`;
      this.querySelectorAll('[data-gallery-image]').forEach(b=>b.setAttribute('aria-pressed',String(b===button)));
      if(button){button.focus({preventScroll:true});this.revealThumbnail(button);}
    }
    revealThumbnail(button,instant=false){
      const strip=button?.closest('.fbp-thumbnails');
      if(!strip)return;
      // Prestige-style alignment of the selected thumbnail, confined to this strip.
      const left=Math.max(0,Math.min(Math.max(0,strip.scrollWidth-strip.clientWidth),button.offsetLeft-(strip.clientWidth-button.offsetWidth)/2));
      const reduce=window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      strip.scrollTo({left,behavior:instant||reduce?'instant':'smooth'});
    }
    disconnectedCallback(){this.pageAbort?.abort();this.galleryResize?.disconnect();this.pageReady=false;}
  }
  customElements.define('fd-bulk-page',FDBulkPage);
})();

/* Content-only carousel: separate from the quantity and pricing controls. */
(() => {
  if(customElements.get('fd-content-carousel'))return;
  class FDContentCarousel extends HTMLElement {
    connectedCallback(){
      if(this.contentAbort)return;
      this.contentAbort=new AbortController();
      this.contentTrack=this.querySelector('[data-content-track]');
      if(!this.contentTrack)return;
      this.cards=[...this.querySelectorAll('[data-content-card]')];
      this.previous=this.querySelector('[data-content-prev]');
      this.next=this.querySelector('[data-content-next]');
      this.counter=this.querySelector('[data-content-status]');
      const on=(target,event,fn,options={})=>target?.addEventListener(event,fn,{...options,signal:this.contentAbort.signal});
      on(this.previous,'click',()=>this.goTo(this.currentIndex()-1));
      on(this.next,'click',()=>this.goTo(this.currentIndex()+1));
      on(this.contentTrack,'scroll',()=>this.updateControls(),{passive:true});
      on(this.contentTrack,'keydown',event=>{
        if(event.target!==this.contentTrack)return;
        const key=event.key;
        if(!['ArrowLeft','ArrowRight','Home','End'].includes(key))return;
        event.preventDefault();
        const index=key==='Home'?0:key==='End'?this.cards.length-1:this.currentIndex()+(key==='ArrowRight'?1:-1);
        this.goTo(index);
      });
      this.contentResize=new ResizeObserver(()=>this.updateControls());
      this.contentResize.observe(this.contentTrack);
      this.updateControls();
    }
    currentIndex(){
      if(!this.cards.length)return 0;
      const left=this.contentTrack.scrollLeft;
      return this.cards.reduce((best,card,index)=>Math.abs(card.offsetLeft-left)<Math.abs(this.cards[best].offsetLeft-left)?index:best,0);
    }
    goTo(index){
      if(!this.cards.length)return;
      index=Math.max(0,Math.min(this.cards.length-1,index));
      const reduce=window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      this.contentTrack.scrollTo({left:this.cards[index].offsetLeft,behavior:reduce?'instant':'smooth'});
    }
    updateControls(){
      const count=this.cards.length,track=this.contentTrack;
      const overflows=track.scrollWidth>track.clientWidth+2;
      this.querySelector('[data-content-controls]').hidden=count<2||!overflows;
      this.previous.disabled=track.scrollLeft<=2;
      this.next.disabled=track.scrollLeft>=track.scrollWidth-track.clientWidth-2;
      const visible=this.cards.map((card,index)=>({card,index})).filter(({card})=>card.offsetLeft>=track.scrollLeft-2&&card.offsetLeft+card.offsetWidth<=track.scrollLeft+track.clientWidth+2);
      const first=visible[0]?.index??this.currentIndex(),last=visible.at(-1)?.index??first;
      this.counter.textContent=count?(first===last?`${first+1} / ${count}`:`${first+1}–${last+1} / ${count}`):'';
    }
    disconnectedCallback(){this.contentAbort?.abort();this.contentResize?.disconnect();this.contentAbort=null;}
  }
  customElements.define('fd-content-carousel',FDContentCarousel);
})();
